import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { NavLink, useNavigate } from "react-router";
import { Box, ButtonBase, Link, Typography, useTheme } from "@mui/material";
import { GoArrowRight } from "react-icons/go";
import { observer } from "mobx-react";

import CoreVirtualizedTable from "../../core/CoreVirtualizedTable";
import StatusChip from "../../../claims/components/table-components/StatusChip";
import { useClaimsStore } from "../../../../hooks/useClaimsStore";
import useNotifier from "../../../../hooks/useNotifier";
import { apiService } from "../../../../services";
import { formatCurrency, formatDisplayDate } from "../../../helpers/dataHelper";
import { Claim } from "../../../../types/claims";
import { SimpleColumnDef } from "../../../../types/table";

const PAGE_SIZE = 20;
const ORDER_BY = { field: "updatedAt", direction: "desc" as const };

export const ExtendedHomeClaimsSection = observer(() => {
  const theme = useTheme();
  const claimsStore = useClaimsStore();

  // The claims table filters assignedUser by the display label from
  // claimsFilterOptions (see ClaimsStore.assignedUsers), not the raw
  // username, so resolve the current user's label from that same source
  // rather than assuming label === username.
  // Not gated on isOptionsLoading: the store initializes it (and
  // clearClaimsStore resets it) to true, so it can't tell "in flight" from
  // "never loaded" and would skip this load unless /claims was visited first.
  useEffect(() => {
    if (claimsStore.assignedUsers.length === 0) {
      claimsStore.loadClaimsFilterOptions();
    }
  }, [claimsStore]);

  const notifier = useNotifier();

  const [claimsData, setClaimsData] = useState<Claim[] | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);

  // Bumped on every new query (username/filters change) so an in-flight
  // loadMore response from a stale query can detect it's no longer current
  // and skip applying its (now-mismatched) page/results.
  const queryIdRef = useRef(0);

  useEffect(() => {
    const queryId = ++queryIdRef.current;
    setIsLoading(true);
    // An in-flight loadMore from the previous query won't clear this itself
    // (its finally is guarded by queryId), so reset it here or infinite
    // scroll stays blocked for the new query.
    setIsLoadingMore(false);
    setCurrentPage(0);
    setHasMore(true);

    apiService
      .getClaimsData(
        0,
        PAGE_SIZE,
        ORDER_BY,
        undefined,
        undefined,
        undefined,
        true,
      )
      .then((response) => {
        if (queryId !== queryIdRef.current) return;
        setClaimsData(response.claims);
        setHasMore(response.hasMore);
      })
      .catch(() => {
        if (queryId !== queryIdRef.current) return;
        setClaimsData(null);
        setHasMore(false);
        notifier.showError("Failed to load claims");
      })
      .finally(() => {
        if (queryId === queryIdRef.current) setIsLoading(false);
      });
  }, []);

  const loadMore = useCallback(() => {
    if (isLoadingMore || !hasMore) return;

    const queryId = queryIdRef.current;
    setIsLoadingMore(true);
    const nextPage = currentPage + 1;

    apiService
      .getClaimsData(
        nextPage,
        PAGE_SIZE,
        ORDER_BY,
        undefined,
        undefined,
        undefined,
        true,
      )
      .then((response) => {
        if (queryId !== queryIdRef.current) return;
        setClaimsData((prev) =>
          prev ? [...prev, ...response.claims] : response.claims,
        );
        setCurrentPage(nextPage);
        setHasMore(response.hasMore);
      })
      .catch(() => {
        if (queryId !== queryIdRef.current) return;
        // Leave hasMore as-is so the user can retry by scrolling again,
        // instead of silently ending pagination on a transient failure.
        notifier.showError("Failed to load more claims");
      })
      .finally(() => {
        if (queryId === queryIdRef.current) setIsLoadingMore(false);
      });
  }, [currentPage, hasMore, isLoadingMore, notifier]);

  const navigate = useNavigate();

  // ClaimsStore's keyword search (the "Search claims..." input) and column
  // filters outlive the Claims page (clearClaimsStore leaves them alone), so
  // the search set here is picked up when /claims mounts. Drop any leftover
  // column filters/tab first so they can't hide the clicked claim.
  const openClaimInClaimsPage = useCallback(
    (claimNumber: string) => {
      claimsStore.clearAllColumnFilters();
      claimsStore.setActiveTab("all");
      claimsStore.setClaimKeywordSearch(claimNumber);
      navigate("/claims");
    },
    [claimsStore, navigate],
  );

  const columns: SimpleColumnDef<Claim>[] = useMemo(
    () => [
      {
        id: "claimNumber",
        header: "Claim",
        accessorKey: "claimNumber",
        width: 170,
        minWidth: 150,
        cell: (value) =>
          value ? (
            <Link
              component="button"
              type="button"
              underline="hover"
              onClick={(event) => {
                event.stopPropagation();
                openClaimInClaimsPage(value);
              }}
              sx={{
                display: "block",
                maxWidth: "100%",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                textAlign: "left",
                fontWeight: 600,
                color: "primary.main",
                fontSize: "14px",
              }}
            >
              {value}
            </Link>
          ) : (
            <Typography>-</Typography>
          ),
      },
      {
        id: "patientName",
        header: "Patient",
        accessorKey: "patientName",
        width: 160,
        minWidth: 120,
        cell: (value) => <Typography noWrap>{value}</Typography>,
      },
      {
        id: "medicalRecordNumber",
        header: "MRN",
        accessorKey: "medicalRecordNumber",
        width: 100,
        minWidth: 90,
        cell: (value) => <Typography>{value || "-"}</Typography>,
      },
      {
        id: "dateOfService",
        header: "DOS",
        accessorKey: "dateOfService",
        width: 110,
        minWidth: 100,
        cell: (value) => <Typography>{formatDisplayDate(value)}</Typography>,
      },
      {
        id: "billedAmount",
        header: "Billed",
        accessorKey: "billedAmount",
        width: 110,
        minWidth: 100,
        cell: (value) => (
          <Typography sx={{ fontWeight: 500, textAlign: "right" }}>
            {formatCurrency(value)}
          </Typography>
        ),
      },
      {
        id: "status",
        header: "Status",
        accessorKey: "status",
        width: 130,
        minWidth: 120,
        enableSorting: false,
        cell: (value) => <StatusChip status={value} fitContent />,
      },
    ],
    [openClaimInClaimsPage],
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
        gap: 1.5,
        width: "100%",
        minWidth: 0,
        padding: 2,
        bgcolor: "background.paper",
        border: "1px solid",
        borderColor: "divider",
        borderRadius: "15px",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 1,
        }}
      >
        <Typography
          variant="h5"
          sx={{ color: "text.primary", fontSize: "16px" }}
        >
          Your Claims
        </Typography>

        <ButtonBase
          component={NavLink}
          to="/claims"
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            borderRadius: "8px",
            px: 1,
            py: 0.5,
            color: theme.palette.custom.brandTeal,
          }}
        >
          <Typography
            sx={{ fontWeight: 600, fontSize: "13px", color: "inherit" }}
          >
            View all claims
          </Typography>
          <GoArrowRight className="w-4 h-4 shrink-0" color="inherit" />
        </ButtonBase>
      </Box>

      <Box
        sx={{
          borderRadius: "8px",
          border: "1px solid",
          borderColor: "divider",
          overflow: "hidden",
        }}
      >
        <CoreVirtualizedTable<Claim>
          isLoading={isLoading}
          data={claimsData || []}
          columns={columns}
          height="360px"
          minWidth={500}
          getRowId={(row) => row.id}
          emptyMessage="No claims found"
          onLoadMore={loadMore}
          hasMore={hasMore}
          isLoadingMore={isLoadingMore}
          enableSorting={false}
          disableFirstOfTypeStyles
          disableLastOfTypeStyles
        />
      </Box>
    </Box>
  );
});
